import { ApolloError, FetchPolicy, gql } from '@apollo/client';
import { apolloClient } from '../apollo/client';
import { dispatchGlobalError } from '../context/ApiErrorContext';

export interface EnderecoDTO {
  id: string;
  cep: string;
  logradouro: string;
  numero: string;
  bairro: string;
  complemento?: string | null;
  cidade: { id: string; nome: string; uf: string };
}

export interface EnderecoInput {
  id?: string;
  cep: string;
  logradouro: string;
  numero: string;
  bairro: string;
  complemento?: string | null;
  cidadeId: string;
}

export interface LocalDTO {
  id: string;
  nome: string;
  descricao?: string | null;
  endereco?: EnderecoDTO | null;
  urlLocalizacao?: string | null;
}

export interface LocalInput {
  nome?: string;
  descricao?: string | null;
  endereco?: EnderecoInput;
  enderecoId?: string;
  urlLocalizacao?: string | null;
}

export const LOCAL_POR_ID = gql`
  query LocalPorId($id: ID!) {
    localPorId(id: $id) {
      id
      nome
      descricao
      urlLocalizacao
      endereco {
        id
        cep
        logradouro
        numero
        bairro
        complemento
        cidade { id nome uf }
      }
    }
  }
`;

interface LocalPorIdData {
  localPorId: any;
}

interface LocalPorIdVars {
  id: string;
}

export const CRIAR_LOCAL_MUTATION = gql`
  mutation CriarLocal($input: LocalInput!) {
    criarLocal(input: $input) {
      id
      nome
      descricao
      urlLocalizacao
      endereco {
        id
        cep
        logradouro
        numero
        bairro
        complemento
        cidade { id nome uf }
      }
    }
  }
`;

interface CriarLocalData {
  criarLocal: any;
}

interface CriarLocalVars {
  input: LocalInput;
}

export const ATUALIZAR_LOCAL_MUTATION = gql`
  mutation AtualizarLocal($id: ID!, $input: LocalInput!) {
    atualizarLocal(id: $id, input: $input) {
      id
      nome
      descricao
      urlLocalizacao
      endereco {
        id
        cep
        logradouro
        numero
        bairro
        complemento
        cidade { id nome uf }
      }
    }
  }
`;

interface AtualizarLocalData {
  atualizarLocal: any;
}

interface AtualizarLocalVars {
  id: string;
  input: LocalInput;
}

const CEP_REGEX = /^\d{5}-\d{3}$/;

function getErrorMessage(error: unknown): string {
  if (error instanceof ApolloError) {
    return (
      error.graphQLErrors[0]?.message ||
      (error.networkError as any)?.message ||
      'Erro inesperado. Tente novamente.'
    );
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Erro inesperado. Tente novamente.';
}

function handleError(error: unknown): never {
  const message = getErrorMessage(error);
  dispatchGlobalError(message);
  throw new Error(message);
}

function sanitizeLocalInput(input: LocalInput): LocalInput {
  const sanitized: LocalInput = {
    nome: input.nome?.trim(),
    descricao: input.descricao?.trim() ?? undefined,
    endereco: input.endereco
      ? {
          id: input.endereco.id,
          cep: input.endereco.cep.trim(),
          logradouro: input.endereco.logradouro.trim(),
          numero: input.endereco.numero.trim(),
          bairro: input.endereco.bairro.trim(),
          complemento: input.endereco.complemento?.trim() ?? null,
          cidadeId: input.endereco.cidadeId,
        }
      : undefined,
    enderecoId: input.endereco ? undefined : input.enderecoId?.trim(),
    urlLocalizacao: input.urlLocalizacao?.trim() ?? null,
  };
  return sanitized;
}

function validateLocalInput(input: LocalInput): void {
  if (input.nome !== undefined && !input.nome.trim()) {
    throw new Error('Nome é obrigatório');
  }
  if (input.endereco) {
    if (!CEP_REGEX.test(input.endereco.cep)) {
      throw new Error('CEP inválido');
    }
    if (!input.endereco.cidadeId) {
      throw new Error('cidadeId é obrigatório');
    }
  }
}

function normalizeLocal(local: any): LocalDTO {
  return {
    id: local.id,
    nome: local.nome,
    descricao: local.descricao ?? null,
    urlLocalizacao: local.urlLocalizacao ?? null,
    endereco: local.endereco
      ? {
          id: local.endereco.id,
          cep: local.endereco.cep,
          logradouro: local.endereco.logradouro,
          numero: local.endereco.numero,
          bairro: local.endereco.bairro,
          complemento: local.endereco.complemento ?? null,
          cidade: {
            id: local.endereco.cidade.id,
            nome: local.endereco.cidade.nome,
            uf: local.endereco.cidade.uf,
          },
        }
      : null,
  };
}

export async function getLocalById(
  id: string,
  opts?: { fetchPolicy?: FetchPolicy },
): Promise<LocalDTO> {
  if (!id) {
    throw new Error('id deve ser uma string não vazia.');
  }
  try {
    const { data } = await apolloClient.query<LocalPorIdData, LocalPorIdVars>({
      query: LOCAL_POR_ID,
      variables: { id },
      fetchPolicy: opts?.fetchPolicy ?? 'cache-first',
    });
    return normalizeLocal(data.localPorId);
  } catch (error) {
    handleError(error);
  }
}

export async function criarLocal(
  input: LocalInput,
  opts?: { fetchPolicy?: FetchPolicy },
): Promise<LocalDTO> {
  validateLocalInput(input);
  const sanitized = sanitizeLocalInput(input);
  try {
    const { data } = await apolloClient.mutate<CriarLocalData, CriarLocalVars>({
      mutation: CRIAR_LOCAL_MUTATION,
      variables: { input: sanitized },
      fetchPolicy: opts?.fetchPolicy as any, // or remove this line if not needed, since fetchPolicy is rarely used for mutations
    });
    const local = normalizeLocal(data?.criarLocal);
    apolloClient.cache.writeQuery<LocalPorIdData, LocalPorIdVars>({
      query: LOCAL_POR_ID,
      variables: { id: local.id },
      data: { localPorId: local },
    });
    return local;
  } catch (error) {
    handleError(error);
  }
}

export async function atualizarLocal(
  id: string,
  input: LocalInput,
): Promise<LocalDTO> {
  if (!id) {
    throw new Error('id deve ser uma string não vazia.');
  }
  validateLocalInput(input);
  const sanitized = sanitizeLocalInput(input);
  try {
    const { data } = await apolloClient.mutate<
      AtualizarLocalData,
      AtualizarLocalVars
    >({
      mutation: ATUALIZAR_LOCAL_MUTATION,
      variables: { id, input: sanitized },
    });
    const local = normalizeLocal(data?.atualizarLocal);
    apolloClient.cache.writeQuery<LocalPorIdData, LocalPorIdVars>({
      query: LOCAL_POR_ID,
      variables: { id: local.id },
      data: { localPorId: local },
    });
    return local;
  } catch (error) {
    handleError(error);
  }
}

export async function upsertLocal(payload: {
  id?: string;
  local: LocalInput;
}): Promise<LocalDTO> {
  if (payload.id) {
    return atualizarLocal(payload.id, payload.local);
  }
  return criarLocal(payload.local);
}

/**
 * @deprecated Utilize atualizarLocal com endereco ou enderecoId em LocalInput.
 */
export async function reassociarEnderecoLocal(
  id: string,
  enderecoId: string,
): Promise<LocalDTO> {
  return atualizarLocal(id, { enderecoId });
}

export {
  LOCAL_POR_ID as LOCAL_POR_ID_QUERY,
};

